import { Box, Typography } from "@mui/material";
import React from "react";
import sportif from "../assets/newImg/sportif.png";
import SEO from "../components/SEO";
import { SX } from "../sx";
import { C, F, R, S, maxW, trans } from "../tokens";

const STATS = [
  {
    value: "1/3",
    label: "des blessures sportives sont d'origine fonctionnelle",
  },
  { value: "60%", label: "des consultations concernent adultes & sportifs" },
  { value: "2×", label: "moins de récidives avec un suivi régulier" },
  { value: "48h", label: "pour récupérer vite après une séance intensive" },
];

const WHY = [
  {
    icon: "🛡️",
    title: "Prévention des blessures",
    desc: "Un corps bien équilibré se blesse moins. En identifiant et corrigeant les compensations posturales, les restrictions articulaires et les déséquilibres chroniques, je dépiste les zones à risque de blessure avant qu'elles ne surviennent.",
  },
  {
    icon: "⚡",
    title: "Optimisation des performances",
    desc: "Libérer les structures du corps (tendons, articulations, muscles, fascias) permet au sportif d'exprimer son plein potentiel. Réduction des tensions internes, plus d'efficacité.",
  },
  {
    icon: "🔄",
    title: "Récupération accélérée",
    desc: "Après un effort intense ou une blessure, l'ostéopathie favorise le retour à l'équilibre. Circulation, réduction de l'inflammation, drainage tissulaire  une aide réelle sur les lendemains de sport.",
  },
  {
    icon: "🎯",
    title: "Retour après blessure",
    desc: "Accompagner le corps dans les reprises et éviter les séquelles : le tissu cicatriciel, les nouvelles tensions, les schémas compensatoires  je travaille pour un retour complet et sûr.",
  },
  {
    icon: "🧘",
    title: "Équilibre corps-esprit",
    desc: "L'accumulation de stress compétitif et d'entraînements intenses crée des tensions chroniques. Le travail ostéopathique agit aussi sur le système nerveux végétatif.",
  },
  {
    icon: "📅",
    title: "Suivi de saison",
    desc: "Un ou deux bilans par saison permettent d'anticiper les pics de fatigue, d'adapter la charge d'entraînement et de maintenir les conditions optimales dans un état compétitif.",
  },
];

const PATHOS = [
  {
    title: "Tendinopathies & douleurs tendineuses",
    badge: "50% des blessés",
    items: [
      "Tendinite rotulienne",
      "Épicondylite",
      "Tendinite achilléenne",
      "Fasciite plantaire",
      "Aponévrosite plantaire",
    ],
  },
  {
    title: "Douleurs articulaires & entorses",
    badge: "35% des blessés",
    items: [
      "Entorse de cheville",
      "Genou du coureur",
      "Épaule du nageur",
      "Douleur d'appui",
      "Séquelles ligamentaires",
    ],
  },
  {
    title: "Douleurs dorsales & lombaires",
    badge: "40% des sportifs",
    items: [
      "Lombalgie chronique du sportif",
      "Douleur sacro-iliaque",
      "Dorsalgie",
      "Hernie discale",
      "Blocage vertébral",
    ],
  },
  {
    title: "Troubles musculaires & crampes",
    badge: "Très fréquent",
    items: [
      "Contractures chroniques",
      "Syndrome des loges",
      "Électrolytes & crampes",
      "Fatigue musculaire profonde",
      "Déséquilibres droite-gauche",
    ],
  },
  {
    title: "Nerfs & douleurs irradiantes",
    badge: "20% des sportifs",
    items: [
      "Course & sport de glisse",
      "Sciatique du sportif",
      "Syndrome du piriforme",
      "Engainements nerveux",
      "Canal carpien fonctionnel",
    ],
  },
  {
    title: "Troubles digestifs & respiration",
    badge: "Bien-être",
    items: [
      "Point de côté récurrent",
      "Reflux à l'effort",
      "Troubles digestifs du coureur",
      "Diaphragme & inspiration",
      "Mobilité des viscères",
    ],
  },
];

const SPORTS = [
  {
    icon: "🚴",
    color: "#E8F0EC",
    title: "Sports d'endurance",
    desc: "Course à pied, triathlon, cyclisme, natation. Gestion des compensations répétitives, des surcharges chroniques et des troubles digestifs à l'effort.",
  },
  {
    icon: "🏋️",
    color: "#F5EBE0",
    title: "Sports de force & fitness",
    desc: "Musculation, CrossFit, haltérophilie, escalade. Travail sur la mobilité articulaire, les déséquilibres musculaires, les douleurs liées à la charge.",
  },
  {
    icon: "⚽",
    color: "#E8EDF5",
    title: "Sports collectifs & raquettes",
    desc: "Football, rugby, tennis, padel, basketball, handball. Récupération, prévention des entorses, gestion des chocs et préparation aux matchs importants.",
  },
];

const STEPS = [
  { num: "01", title: "Anamnèse sportive", duration: "5 min" },
  { num: "02", title: "Bilan postural & fonctionnel", duration: "10 min" },
  { num: "03", title: "Traitement ostéopathique", duration: "35 min" },
  { num: "04", title: "Conseils & programme", duration: "10 min" },
];

const WHEN = [
  {
    icon: "🔴",
    title: "Douleur aiguë ou récente",
    desc: "Une douleur qui gêne l'entraînement, une entorse récente, un blocage vertébral  ne laissez pas s'installer.",
  },
  {
    icon: "📋",
    title: "Douleur récurrente ou chronique",
    desc: "La même zone douloureuse revient, toujours au même moment. Il est souvent signe d'un dysfonctionnement fonctionnel à corriger.",
  },
  {
    icon: "⬆️",
    title: "Avant une compétition importante",
    desc: "Une séance 3 à 5 semaines avant la compétition pour optimiser votre état physique, potentiellement la plus pertinente de la saison.",
  },
  {
    icon: "🔁",
    title: "Reprise après coupure ou blessure",
    desc: "Évaluer les séquelles, les nouvelles tensions et mettre en place un protocole de reprise adapté. Se remettre en route de façon intelligente.",
  },
  {
    icon: "📅",
    title: "Suivi préventif de saison",
    desc: "Un ou deux bilans par saison chez un ostéopathe de confiance contribuent à maintenir un état optimal et à éviter les blessures répétitives.",
  },
];

const Sportifs = () => (
  <>
    <SEO
      title="Ostéopathie sportive  Récupération & performance  Balma"
      description="Tendinites, entorses, syndrome de l'essuie-glace, préparation physique, récupération post-blessure. Ostéopathie du sport avec Maxime Nicolaieff à Balma (31130)."
      path="/pathologies/sportifs"
      schema={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Accueil",
            item: "https://www.balma-osteopathe.fr/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Pathologies",
            item: "https://www.balma-osteopathe.fr/pathologies",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Sportifs",
            item: "https://www.balma-osteopathe.fr/pathologies/sportifs",
          },
        ],
      }}
    />
    {/* ── Hero ─────────────────────────────────── */}
    <Box component="section" sx={{ pt: "68px", background: C.bg }}>
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          pt: { xs: "3.5rem", md: "5rem" },
          pb: { xs: "2.5rem", md: "4rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 340px" },
          gap: { xs: "2rem", md: "4rem" },
          alignItems: "center",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Sportifs &amp; performance
          </Box>
          <Typography
            variant="h1"
            component="h1"
            sx={{ ...SX.displayTitleLg, mt: ".5rem", mb: "1.25rem" }}
          >
            Performer sans douleur,
            <br />
            <em>durer dans le temps.</em>
          </Typography>
          <Typography
            sx={{
              fontSize: ".95rem",
              color: C.textMuted,
              maxWidth: "520px",
              lineHeight: 1.75,
              mb: "2rem",
            }}
          >
            Quelle que soit votre discipline, votre corps subit des contraintes
            répétitives. L'ostéopathie accompagne chaque étape prévention,
            récupération, retour après blessure.
          </Typography>
          <Box
            component="a"
            href="https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"
            target="_blank"
            rel="noreferrer"
            sx={SX.btnDark}
          >
            Prendre rendez-vous
          </Box>
        </Box>
        <Box
          sx={{
            width: "100%",
            height: { xs: "220px", md: "360px" },
            borderRadius: R.lg,
            overflow: "hidden",
            boxShadow: S.md,
            display: { xs: "none", md: "block" },
          }}
        >
          <Box
            component="img"
            src={sportif}
            alt="Ostéopathie pour sportifs"
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      </Box>
    </Box>

    {/* ── Stats ────────────────────────────────── */}
    <Box component="section" sx={{ background: C.dark, py: "3.5rem" }}>
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: { xs: "1.5rem", md: "2rem" },
        }}
      >
        {STATS.map((s) => (
          <Box key={s.label}>
            <Typography
              sx={{
                fontFamily: F.display,
                fontSize: "2.4rem",
                fontWeight: 300,
                color: C.accent,
                lineHeight: 1,
                mb: ".35rem",
              }}
            >
              {s.value}
            </Typography>
            <Typography
              sx={{
                fontSize: ".78rem",
                color: "rgba(255,255,255,.5)",
                lineHeight: 1.5,
              }}
            >
              {s.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>

    {/* ── Pourquoi consulter ───────────────────── */}
    <Box component="section" sx={{ py: "6rem" }}>
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box sx={{ mb: "3rem" }}>
          <Box component="span" sx={SX.sectionLabel}>
            Pourquoi consulter
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{ ...SX.displayTitle, maxWidth: "500px" }}
          >
            Le sport sollicite, <em>l'ostéopathie restaure.</em>
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
            },
            gap: "1.25rem",
          }}
        >
          {WHY.map((w) => (
            <Box
              key={w.title}
              sx={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: R.lg,
                padding: "1.5rem",
                transition: `box-shadow ${trans}`,
                "&:hover": { boxShadow: S.md },
              }}
            >
              <Box sx={{ fontSize: "1.4rem", mb: ".75rem" }}>{w.icon}</Box>
              <Typography
                sx={{
                  fontSize: ".9rem",
                  fontWeight: 600,
                  color: C.text,
                  mb: ".5rem",
                }}
              >
                {w.title}
              </Typography>
              <Typography
                sx={{ fontSize: ".8rem", color: C.textMuted, lineHeight: 1.65 }}
              >
                {w.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Pathologies traitées ─────────────────── */}
    <Box component="section" sx={{ background: C.bgAlt, py: "6rem" }}>
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box component="span" sx={SX.sectionLabel}>
          Pathologies traitées
        </Box>
        <Typography
          variant="h2"
          component="h2"
          sx={{ ...SX.displayTitle, mb: "2.5rem" }}
        >
          Ce que je soigne, <em>au quotidien.</em>
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: "1.25rem",
            alignItems: "start",
          }}
        >
          {PATHOS.map((p) => (
            <Box
              key={p.title}
              sx={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: R.lg,
                padding: "1.5rem",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "1rem",
                  mb: "1rem",
                }}
              >
                <Typography
                  sx={{ fontSize: ".9rem", fontWeight: 600, color: C.text }}
                >
                  {p.title}
                </Typography>
                <Box
                  component="span"
                  sx={{
                    fontSize: ".6rem",
                    fontWeight: 600,
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    padding: ".2rem .55rem",
                    borderRadius: "4px",
                    background: "rgba(196,97,76,.1)",
                    color: C.accent,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {p.badge}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: ".3rem",
                  mb: "1.25rem",
                }}
              >
                {p.items.map((item) => (
                  <Typography
                    key={item}
                    sx={{
                      fontSize: ".78rem",
                      color: C.textMuted,
                      pl: ".75rem",
                      borderLeft: `2px solid ${C.border}`,
                      lineHeight: 1.4,
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
              <Box
                component="a"
                href="https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"
                target="_blank"
                rel="noreferrer"
                sx={{
                  display: "inline-block",
                  padding: ".45rem 1rem",
                  background: C.dark,
                  color: "#fff",
                  borderRadius: "50px",
                  fontSize: ".75rem",
                  fontWeight: 500,
                  textDecoration: "none",
                  transition: `background ${trans}`,
                  "&:hover": { background: C.primary },
                }}
              >
                Prendre RDV
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Sports adaptés ───────────────────────── */}
    <Box component="section" sx={{ py: "6rem" }}>
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box component="span" sx={SX.sectionLabel}>
          Tous les sports
        </Box>
        <Typography
          variant="h2"
          component="h2"
          sx={{ ...SX.displayTitle, mb: "2.5rem" }}
        >
          Quelque sport que vous pratiquiez, <em>je m'adapte.</em>
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: "1.25rem",
          }}
        >
          {SPORTS.map((s) => (
            <Box
              key={s.title}
              sx={{
                borderRadius: R.lg,
                overflow: "hidden",
                background: C.bgAlt,
                border: `1px solid ${C.border}`,
              }}
            >
              <Box
                sx={{
                  height: "120px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "2.5rem",
                  background: s.color,
                }}
              >
                {s.icon}
              </Box>
              <Box sx={{ padding: "1.25rem" }}>
                <Typography
                  sx={{
                    fontSize: ".9rem",
                    fontWeight: 600,
                    color: C.text,
                    mb: ".5rem",
                  }}
                >
                  {s.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".8rem",
                    color: C.textMuted,
                    lineHeight: 1.65,
                  }}
                >
                  {s.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Déroulement ──────────────────────────── */}
    <Box component="section" sx={{ background: C.bgAlt, py: "6rem" }}>
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "360px 1fr" },
          gap: { xs: "3rem", md: "5rem" },
          alignItems: "start",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            1 séance sportif
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{ ...SX.displayTitle, mb: "1rem" }}
          >
            60 minutes, <em>performance d'abord.</em>
          </Typography>
          <Typography
            sx={{ fontSize: ".88rem", color: C.textMuted, lineHeight: 1.75 }}
          >
            Consultation ancrée dans votre discipline et votre niveau. Je prends
            en compte votre programme d'entraînement, vos compétitions à venir
            et votre saison pour adapter l'intensité de la prise en charge.
          </Typography>
        </Box>
        <Box>
          {STEPS.map((s, i) => (
            <Box
              key={s.num}
              sx={{
                display: "grid",
                gridTemplateColumns: "48px 1fr auto",
                gap: "1rem",
                alignItems: "baseline",
                py: "1.25rem",
                borderBottom:
                  i < STEPS.length - 1 ? `1px solid ${C.border}` : "none",
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: F.display,
                  fontSize: "1.5rem",
                  fontWeight: 300,
                  color: C.accent,
                  lineHeight: 1,
                }}
              >
                {s.num}
              </Box>
              <Box
                component="span"
                sx={{ fontSize: ".9rem", fontWeight: 600, color: C.text }}
              >
                {s.title}
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: ".72rem",
                  color: C.textMuted,
                  whiteSpace: "nowrap",
                }}
              >
                {s.duration}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Quand consulter ──────────────────────── */}
    <Box component="section" sx={{ py: "6rem" }}>
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 400px" },
          gap: "4rem",
          alignItems: "start",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Bonne consultation
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{ ...SX.displayTitle, mb: "2rem" }}
          >
            Les bons moments pour <em>prendre RDV.</em>
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
            {WHEN.map((w) => (
              <Box
                key={w.title}
                sx={{
                  display: "flex",
                  gap: ".75rem",
                  alignItems: "flex-start",
                  fontSize: ".85rem",
                  color: C.textMuted,
                  lineHeight: 1.6,
                }}
              >
                <Box
                  component="span"
                  sx={{ fontSize: "1rem", flexShrink: 0, mt: ".1rem" }}
                >
                  {w.icon}
                </Box>
                <Box>
                  <Box
                    component="strong"
                    sx={{
                      fontSize: ".85rem",
                      color: C.text,
                      display: "block",
                      mb: ".2rem",
                      fontWeight: 600,
                    }}
                  >
                    {w.title}
                  </Box>
                  <Box component="span">{w.desc}</Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ background: C.dark, borderRadius: R.lg, padding: "2rem" }}>
          <Typography
            sx={{
              fontSize: ".62rem",
              fontWeight: 600,
              letterSpacing: "2px",
              textTransform: "uppercase",
              color: C.accent,
              mb: ".75rem",
            }}
          >
            Prêt à optimiser votre corps ?
          </Typography>
          <Typography
            variant="h3"
            component="h3"
            sx={{
              fontFamily: F.display,
              fontSize: "1.6rem",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1.2,
              mb: ".75rem",
              "& em": { fontStyle: "italic", color: "rgba(255,255,255,.6)" },
            }}
          >
            Disponible à Balma &amp; <em>Castelnau.</em>
          </Typography>
          <Typography
            sx={{
              fontSize: ".82rem",
              color: "rgba(255,255,255,.5)",
              lineHeight: 1.7,
              mb: "1.5rem",
            }}
          >
            Consultations disponibles du lundi au samedi, à Balma et
            Castelnau-d'Estrétefonds. Séances à domicile dans un rayon de 20 km.
          </Typography>
          <Box
            component="a"
            href="https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"
            target="_blank"
            rel="noreferrer"
            sx={SX.btnAccent}
          >
            Réserver sur Doctolib
          </Box>
          <Box
            component="a"
            href="tel:0695788983"
            sx={{
              display: "block",
              fontSize: ".75rem",
              color: "rgba(255,255,255,.35)",
              mt: ".75rem",
              textDecoration: "none",
            }}
          >
            ou 06 95 78 89 83
          </Box>
        </Box>
      </Box>
    </Box>
  </>
);

export default Sportifs;
